import {
  FP_CUSTOM_SOURCE_PREFIX,
  FP_FIELD_SOURCES,
  FP_TOTAL_WEIGHT_SOURCE_PREFIX,
  type Address,
  type FpFieldSource,
  type PublicQuote,
} from "@forgepoint/shared";
import {
  formatAddressLines,
  getQuoteEffectiveDate,
  getTotalWeightsByUnit,
  publicQuotePeopleEmails,
  publicQuotePeopleNames,
} from "@/modules/publicQuote/utils";
import { getQuoteDisplayTotals } from "@/lib/quoteCurrency";
import {
  lookupCustomField,
  normalizeCustomFields,
} from "./customFieldResolution";
import { formatLongDate, formatLongDateOnly } from "@/lib/formatDate";

/** The value format of a quote field source. */
export type QuoteFieldFormat = "text" | "markdown";

/** One field usable as an fp-field / fp-markdown `source`. */
interface QuoteFieldDefinition {
  label: string;
  format: QuoteFieldFormat;
  resolve: (quote: PublicQuote) => string;
}

/**
 * A single-line display address, or "" when unset.
 * @param address - The address to format, or null/undefined when absent
 * @returns The comma-joined address line, or "" when absent
 */
function addressLine(address: Address | null | undefined): string {
  return formatAddressLines(address)?.join(", ") ?? "";
}

/**
 * The organization's single-line display address, or "" when unset.
 * @param quote - The public quote backing the document
 * @returns The comma-joined address line, or "" when absent
 */
function orgAddressLine(quote: PublicQuote): string {
  return addressLine(quote.organization?.address);
}

/** Field definitions keyed by the stored-document `source` vocabulary. */
const FIELD_DEFINITIONS: Record<FpFieldSource, QuoteFieldDefinition> = {
  "org-name": {
    label: "Organization name",
    format: "text",
    resolve: (quote) => quote.organization?.name ?? "",
  },
  "org-address": {
    label: "Organization address",
    format: "text",
    resolve: orgAddressLine,
  },
  "org-website": {
    label: "Organization website",
    format: "text",
    resolve: (quote) => quote.organization?.website ?? "",
  },
  "org-phone": {
    label: "Organization phone",
    format: "text",
    resolve: (quote) => quote.organization?.phone ?? "",
  },
  "org-fax": {
    label: "Organization fax",
    format: "text",
    resolve: (quote) => quote.organization?.fax ?? "",
  },
  "org-headquarters": {
    label: "Organization headquarters",
    format: "text",
    resolve: (quote) => {
      const address = orgAddressLine(quote);
      if (address === "") {
        return "";
      }
      const name = quote.organization?.name ?? "";
      return `${name} Headquarters: ${address}`.trim();
    },
  },
  "org-contact-line": {
    label: "Organization contact line",
    format: "text",
    // Only present values render, so an empty website or phone never leaves a
    // dangling separator; non-breaking spaces keep the wide pipe spacing.
    resolve: (quote) =>
      [quote.organization?.website ?? "", quote.organization?.phone ?? ""]
        .filter((value) => value !== "")
        .join("  |  "),
  },
  location: {
    label: "Location",
    format: "text",
    resolve: (quote) => quote.location?.name ?? "",
  },
  "location-name": {
    label: "Location name",
    format: "text",
    resolve: (quote) => quote.location?.name ?? "",
  },
  "location-address": {
    label: "Location address",
    format: "text",
    resolve: (quote) => addressLine(quote.location?.address),
  },
  "letterhead-name": {
    label: "Letterhead name",
    format: "text",
    resolve: (quote) => {
      const locationName = quote.location?.name.trim() ?? "";
      return locationName !== ""
        ? locationName
        : (quote.organization?.name ?? "");
    },
  },
  "letterhead-address": {
    label: "Letterhead address",
    format: "text",
    resolve: (quote) => {
      const locationAddress = addressLine(quote.location?.address);
      return locationAddress !== "" ? locationAddress : orgAddressLine(quote);
    },
  },
  "sales-rep-name": {
    label: "Sales rep name",
    format: "text",
    resolve: (quote) => quote.salesRepName ?? "",
  },
  "sales-rep-email": {
    label: "Sales rep email",
    format: "text",
    resolve: (quote) => quote.salesRepEmail ?? "",
  },
  "estimator-names": {
    label: "Estimator names",
    format: "text",
    resolve: (quote) => quote.estimatorNames.join(", "),
  },
  "customer-name": {
    label: "Customer name",
    format: "text",
    resolve: (quote) => quote.customerCompany?.name ?? "",
  },
  "customer-company-name": {
    label: "Customer company name",
    format: "text",
    resolve: (quote) => quote.customerCompany?.name ?? "",
  },
  "customer-company-phone": {
    label: "Customer company phone",
    format: "text",
    resolve: (quote) => quote.customerCompany?.phone ?? "",
  },
  "customer-company-email": {
    label: "Customer company email",
    format: "text",
    resolve: (quote) => quote.customerCompany?.email ?? "",
  },
  "customer-company-address": {
    label: "Customer company address",
    format: "text",
    resolve: (quote) => addressLine(quote.customerCompany?.address),
  },
  "quote-people-names": {
    label: "Quote people names",
    format: "text",
    resolve: publicQuotePeopleNames,
  },
  "quote-people-emails": {
    label: "Quote people emails",
    format: "text",
    resolve: publicQuotePeopleEmails,
  },
  "ship-to-address": {
    label: "Ship-to address",
    format: "text",
    resolve: (quote) => addressLine(quote.shippingAddress),
  },
  "opportunity-name": {
    label: "Opportunity name",
    format: "text",
    resolve: (quote) => quote.opportunity?.name ?? "",
  },
  "quote-name": {
    label: "Quote name",
    format: "text",
    resolve: (quote) => quote.name ?? "",
  },
  "quote-number": {
    label: "Quote number",
    format: "text",
    resolve: (quote) => quote.quoteDisplayNumber,
  },
  "effective-date": {
    label: "Effective date",
    format: "text",
    resolve: (quote) => formatLongDateOnly(getQuoteEffectiveDate(quote)),
  },
  "expiration-date": {
    label: "Expiration date",
    format: "text",
    resolve: (quote) => formatLongDateOnly(quote.expirationDate),
  },
  "quote-footer": {
    label: "Quote footer",
    format: "text",
    resolve: (quote) => quote.quoteFooter ?? "",
  },
  subtotal: {
    label: "Subtotal",
    format: "text",
    resolve: (quote) =>
      quote.showTotals ? getQuoteDisplayTotals(quote).subtotalFormatted : "",
  },
  "subtotal-with-discounts": {
    label: "Subtotal with discounts",
    format: "text",
    resolve: (quote) =>
      quote.showTotals
        ? getQuoteDisplayTotals(quote).subtotalWithDiscountsFormatted
        : "",
  },
  total: {
    label: "Total",
    format: "text",
    resolve: (quote) =>
      quote.showTotals ? getQuoteDisplayTotals(quote).totalFormatted : "",
  },
  "signer-name": {
    label: "Signer name",
    format: "text",
    resolve: (quote) => quote.signature?.name ?? "",
  },
  "signed-date": {
    label: "Signed date",
    format: "text",
    resolve: (quote) =>
      quote.signature ? formatLongDate(quote.signature.signedAt) : "",
  },
  "cover-letter": {
    label: "Cover letter",
    format: "markdown",
    // A disabled cover letter resolves empty so the show-when condition,
    // hide-empty, and the page break all drop the section, matching the legacy
    // public templates.
    resolve: (quote) =>
      quote.enableCoverLetter ? (quote.coverLetterMarkdown ?? "") : "",
  },
  "payment-terms": {
    label: "Payment terms",
    format: "markdown",
    resolve: (quote) => quote.paymentTerms ?? "",
  },
  "delivery-terms": {
    label: "Delivery terms",
    format: "markdown",
    resolve: (quote) => quote.deliveryTerms ?? "",
  },
  "terms-of-service": {
    label: "Terms of service",
    format: "markdown",
    resolve: (quote) => quote.termsOfService ?? "",
  },
};

/**
 * Every field key usable as an fp-field / fp-markdown `source`, in a stable
 * order for pickers (block palette field list, properties-panel dropdown).
 */
export const QUOTE_FIELD_SOURCES: readonly FpFieldSource[] = FP_FIELD_SOURCES;

/** A pickable quote field: its `source` key, friendly label, and format. */
export interface QuoteFieldOption {
  /** The fp-field `source` attribute value (a `custom:` key for custom fields). */
  source: string;
  /** Friendly label shown in field pickers. */
  label: string;
  /** Whether the field's value is plain text or multiline markdown. */
  format: QuoteFieldFormat;
  className?: string;
  styleAttr?: string;
}

/** Every pickable STANDARD field with its friendly label, in picker order. */
export const QUOTE_FIELD_OPTIONS: readonly QuoteFieldOption[] =
  FP_FIELD_SOURCES.map((source) => ({
    source,
    label: FIELD_DEFINITIONS[source].label,
    format: FIELD_DEFINITIONS[source].format,
  }));

/**
 * Builds the full pickable-field list: the standard fields followed by the
 * org's custom fields (each a text-format `custom:<name>` source labelled by
 * its name), in configured order.
 * @param customFieldNames - The org's quote-level custom field names
 * @returns The standard options plus one option per custom field
 */
export function buildQuoteFieldOptions(
  customFieldNames: readonly string[] = [],
): QuoteFieldOption[] {
  return [...QUOTE_FIELD_OPTIONS, ...customFieldNames.map(customFieldOption)];
}

/**
 * Whether a field source names a custom (org-defined) field rather than a
 * built-in one, so pickers can mark it as custom.
 * @param source - The fp-field `source` value
 * @returns True when the source is a `custom:` key
 */
export function isCustomFieldSource(source: string): boolean {
  return source.startsWith(FP_CUSTOM_SOURCE_PREFIX);
}

/**
 * Builds the pickable option for a custom quote field. Custom values are keyed
 * by name and always render as plain text, so the name doubles as the label.
 * @param name - The custom field name
 * @returns The option carrying a `custom:<name>` source
 */
function customFieldOption(name: string): QuoteFieldOption {
  return {
    source: `${FP_CUSTOM_SOURCE_PREFIX}${name}`,
    label: name,
    format: "text",
  };
}

/** Class rendering text in the cursive authored-signature face. */
export const SIGNATURE_FONT_CLASS = "font-signature";

/**
 * Inline-only slash-menu option inserting the sales-rep name styled as a
 * cursive authored signature. Not part of QUOTE_FIELD_OPTIONS: source pickers
 * (properties panel dropdown) select data bindings, where a styled duplicate
 * of `sales-rep-name` would be noise.
 */
const SIGNATURE_FIELD_OPTION: QuoteFieldOption = {
  source: "sales-rep-name",
  label: "Sales rep signature",
  format: "text",
  className: SIGNATURE_FONT_CLASS,
  styleAttr: "font-size: 24px; line-height: 1",
};

/**
 * Filters the fields pickable as INLINE chips by a case-insensitive substring
 * match on the friendly label or the source key (the slash menu's typed
 * query). Custom fields are included; markdown-format fields are excluded: a
 * multiline markdown value makes no sense as an inline chip, so those fields
 * insert only as blocks via the palette (where they land as bound
 * `<fp-markdown>` elements).
 * @param query - The typed filter text ("" matches every text-format field)
 * @param customFieldNames - The org's quote-level custom field names
 * @returns The matching text-format options in stable picker order
 */
export function filterQuoteFieldOptions(
  query: string,
  customFieldNames: readonly string[] = [],
): QuoteFieldOption[] {
  const needle = query.trim().toLowerCase();
  return [
    ...buildQuoteFieldOptions(customFieldNames),
    SIGNATURE_FIELD_OPTION,
  ].filter(
    (option) =>
      option.format === "text" &&
      (option.label.toLowerCase().includes(needle) ||
        option.source.includes(needle)),
  );
}

/**
 * Resolves a field key to its friendly picker label, or null when unknown.
 * @param source - The field key from the document, or null when unset
 * @returns The friendly label, or null for unknown/unset keys
 */
export function quoteFieldLabel(source: string | null): string | null {
  if (source === null) {
    return null;
  }
  if (source.startsWith(FP_CUSTOM_SOURCE_PREFIX)) {
    return source.slice(FP_CUSTOM_SOURCE_PREFIX.length);
  }
  if (!isFpFieldSource(source)) {
    return null;
  }
  return FIELD_DEFINITIONS[source].label;
}

/**
 * Whether a field key resolves to a markdown-format source.
 * @param source - The field key from the document, or null when unset
 * @returns True when the key names a markdown-format field
 */
export function isMarkdownQuoteField(source: string | null): boolean {
  if (source === null || !isFpFieldSource(source)) {
    return false;
  }
  return FIELD_DEFINITIONS[source].format === "markdown";
}

/**
 * Resolves a stored-document field key to its value on the quote, or null.
 * @param quote - The public quote backing the document
 * @param source - The field key from the document, or null when unset
 * @returns The field value, or null when the key is missing or unknown
 */
export function resolveQuoteField(
  quote: PublicQuote,
  source: string | null,
): string | null {
  if (!source) {
    return null;
  }
  if (source.startsWith(FP_CUSTOM_SOURCE_PREFIX)) {
    return lookupCustomField(
      normalizeCustomFields(quote.customFields),
      source.slice(FP_CUSTOM_SOURCE_PREFIX.length),
    );
  }
  if (source.startsWith(FP_TOTAL_WEIGHT_SOURCE_PREFIX)) {
    return resolveTotalWeight(
      quote,
      source.slice(FP_TOTAL_WEIGHT_SOURCE_PREFIX.length).trim(),
    );
  }
  return isFpFieldSource(source)
    ? FIELD_DEFINITIONS[source].resolve(quote)
    : null;
}

/** Matches an inline `{{ source }}` field token in authored markdown. */
const FIELD_TOKEN_PATTERN = /\{\{\s*([^{}]+?)\s*\}\}/g;

/**
 * Tailwind classes for the orange placeholder chip shown for an unfilled field,
 * shared by the inline `<fp-field>` renderer and the note markdown renderer.
 * Its colors are important-flagged so the block's authored inline color and
 * background (spread onto the same element) never restyle the chip.
 */
export const FIELD_PLACEHOLDER_CHIP_CLASS =
  "!text-orange-700 !bg-orange-50 border border-dashed border-orange-300 rounded px-1 text-[11px]";

/** A literal text run, or a `{{ source }}` field token, from a split. */
export type FieldTokenSegment =
  | { readonly text: string }
  | { readonly source: string };

/**
 * Splits markdown into literal-text and field-token segments, in order, so a
 * renderer can bind fields mid-sentence. Renderer-agnostic: the caller decides
 * how a resolved or unfilled token is displayed.
 * @param input - The markdown text to split
 * @returns The ordered text/token segments
 */
export function splitFieldTokens(input: string): FieldTokenSegment[] {
  const matches = [...input.matchAll(FIELD_TOKEN_PATTERN)];
  const { segments, end } = matches.reduce<{
    segments: FieldTokenSegment[];
    end: number;
  }>(
    (acc, match) => {
      const start = match.index ?? 0;
      const lead =
        start > acc.end ? [{ text: input.slice(acc.end, start) }] : [];
      return {
        segments: [...acc.segments, ...lead, { source: match[1].trim() }],
        end: start + match[0].length,
      };
    },
    { segments: [], end: 0 },
  );
  return end < input.length
    ? [...segments, { text: input.slice(end) }]
    : segments;
}

/**
 * Whether a raw source key is one of the quote-scope field sources.
 * @param source - The raw source key to test
 * @returns True when the key is a known quote-scope field source
 */
function isFpFieldSource(source: string): source is FpFieldSource {
  return FP_FIELD_SOURCES.some((candidate) => candidate === source);
}

/**
 * Resolves a `total-weight:<unit>` source: the quote's summed weight for that
 * unit, or "" when weight is hidden or the unit has no weight.
 * @param quote - The public quote backing the document
 * @param unit - The authored weight-unit parameter
 * @returns The total weight fixed to two decimals, or "" when absent
 */
function resolveTotalWeight(quote: PublicQuote, unit: string): string {
  if (!quote.showTotalWeight) {
    return "";
  }
  const entry = getTotalWeightsByUnit(quote.lines).find(
    ([weightUnit]) => weightUnit === unit,
  );
  return entry ? entry[1].toFixed(2) : "";
}
